import { useEffect } from 'react';
import { get } from '../api/commonAPI';

/**
 * Custom Hook: useMetaTags
 * Fetches meta tags from the backend API and injects them directly into the document head
 * 
 * Usage:
 * useMetaTags('blog', 'my-blog-slug');  // Fetch meta tags for a blog post
 * useMetaTags('default');               // Use default meta tags
 * 
 * Note: Uses the same pattern as fetchBlogBySlug - calls get('ui/meta-tags/...')
 * which properly routes through the API Gateway
 */
const useMetaTags = (type, identifier = null) => {
  useEffect(() => {
    const fetchAndInjectMetaTags = async () => {
      try {
        let path = 'ui/meta-tags/';
        
        if (type === 'blog' && identifier) {
          path += `blog/${identifier}`;
        } else if (type === 'space' && identifier) {
          path += `space/${identifier}`;
        } else {
          path += 'default';
        }

        console.log('📡 Fetching meta tags from path:', path);
        const response = await get(path);
        
        if (response && response.status === 200 && response.data) {
          console.log('✅ Meta tags received from API:', response.data);
          injectMetaTags(response.data);
        } else {
          console.warn('⚠️ Unexpected response from meta tags API:', response?.status);
          
          // Fall back to default
          try {
            console.log('📡 Fetching default meta tags');
            const defaultResponse = await get('ui/meta-tags/default');
            if (defaultResponse && defaultResponse.status === 200 && defaultResponse.data) {
              console.log('✅ Using default meta tags as fallback');
              injectMetaTags(defaultResponse.data);
            }
          } catch (defaultError) {
            console.error('❌ Failed to fetch default meta tags:', defaultError);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching meta tags:', error);
        
        // Fall back to default tags
        try {
          console.log('📡 Fetching default meta tags as error fallback');
          const defaultResponse = await get('ui/meta-tags/default');
          if (defaultResponse && defaultResponse.status === 200 && defaultResponse.data) {
            console.log('✅ Using default meta tags');
            injectMetaTags(defaultResponse.data);
          }
        } catch (defaultError) {
          console.error('❌ Error fetching default meta tags:', defaultError);
        }
      }
    };

    const injectMetaTags = (metaTags) => {
      if (!metaTags) return;

      // Helper function to FORCEFULLY update, create, or remove meta tag
      const updateMetaTag = (name, content, property = false) => {
        let meta;
        
        if (property) {
          // For property attributes (og:*, article:*)
          meta = document.querySelector(`meta[property="${name}"]`);
          
          if (!content) {
            // Remove the tag if no content provided
            if (meta) {
              meta.remove();
            }
            return;
          }
          
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('property', name);
            document.head.appendChild(meta);
          }
        } else {
          // For name attributes (twitter:*, description, keywords)
          meta = document.querySelector(`meta[name="${name}"]`);
          
          if (!content) {
            // Remove the tag if no content provided
            if (meta) {
              meta.remove();
            }
            return;
          }
          
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', name);
            document.head.appendChild(meta);
          }
        }
        
        // FORCE update the content
        meta.setAttribute('content', content);
      };

      // Update title - ALWAYS override
      if (metaTags.title) {
        document.title = metaTags.title;
        console.log('✅ Meta title updated:', metaTags.title);
      }
      
      // Update basic meta tags
      updateMetaTag('description', metaTags.description);
      
      // Update Open Graph tags
      updateMetaTag('og:type', metaTags['og:type'], true);
      updateMetaTag('og:title', metaTags['og:title'], true);
      updateMetaTag('og:description', metaTags['og:description'], true);
      updateMetaTag('og:image', metaTags['og:image'], true);
      updateMetaTag('og:image:secure_url', metaTags['og:image:secure_url'], true);
      updateMetaTag('og:image:width', metaTags['og:image:width'], true);
      updateMetaTag('og:image:height', metaTags['og:image:height'], true);
      updateMetaTag('og:image:type', metaTags['og:image:type'], true);
      updateMetaTag('og:site_name', metaTags['og:site_name'], true);
      updateMetaTag('og:locale', metaTags['og:locale'], true);
      updateMetaTag('og:url', metaTags['og:url'], true);
      
      // Update Twitter Card tags
      updateMetaTag('twitter:card', metaTags['twitter:card']);
      updateMetaTag('twitter:site', metaTags['twitter:site']);
      updateMetaTag('twitter:title', metaTags['twitter:title']);
      updateMetaTag('twitter:description', metaTags['twitter:description']);
      updateMetaTag('twitter:image', metaTags['twitter:image']);
      
      // Update or REMOVE Article tags (important for non-blog pages)
      updateMetaTag('article:published_time', metaTags['article:published_time'], true);
      updateMetaTag('article:modified_time', metaTags['article:modified_time'], true);
      updateMetaTag('article:author', metaTags['article:author'], true);
      
      console.log('✅ Meta tags injected successfully');
    };
    
    fetchAndInjectMetaTags();
  }, [type, identifier]);
};

export default useMetaTags;
